import { z } from 'zod';
import { PAYOUT_KIND } from '../vocab/javelin';

// «Дашборд выплат» (ТЗ 2026-08-28) — отдельный слой поверх экономики:
// итерационные (по стажу) и недельные тиры модераторам, реферальные бонусы. Правила —
// apps/api/src/javelin/payouts_rules.ts; агрегации — payouts.ts.

export const JavPayoutsDashboardQuerySchema = z.object({
  iteration: z.string().trim().default(''),
});
export type JavPayoutsDashboardQuery = z.infer<typeof JavPayoutsDashboardQuerySchema>;

/** Период (итерация целиком или неделя Пн–Вс) одного модератора. */
export const JavPayoutPeriodSchema = z.looseObject({
  key: z.string(),           // итерация ('1000') | '2026-08-24' (понедельник недели)
  start: z.string(),         // 'YYYY-MM-DD' (итерация: первая/последняя дата сессий, '' если нет)
  end: z.string(),
  sessions: z.number(),      // все, кроме rejected/cancelled — база для порогов и суммы
  accepted: z.number(),
  pending: z.number(),
  rejected: z.number(),
  cancelled: z.number(),
  unstatused: z.number(),    // ещё без вердикта
  rate: z.number(),          // kpiPercent, 1 знак
  add_per_session: z.number(), // надбавка к ставке $
  bonus_usd: z.number(),     // add_per_session × sessions
  base_usd: z.number(),      // Σ базы по датам ($50 до 29.08.2026, $60 после) — справочно
  paid_usd: z.number(),      // из леджера за этот период (итерация: kpi_bonus от «Проект завершён»)
  paid_tx_id: z.string(),    // '' если не зафиксировано (итерация: id kpi_bonus — дашбордом не удаляется)
});
export type JavPayoutPeriod = z.infer<typeof JavPayoutPeriodSchema>;

export const JavPayoutReferralSchema = z.looseObject({
  kind: z.enum(['referral_day', 'referral_iter']),
  referrer_key: z.string(),          // sha256(нормализованный телефон)[0..32] — ключ, не PII
  referrer_phone_masked: z.string(), // для показа
  referrer_name: z.string(),
  mod_id: z.coerce.string(),         // кому в бюджет (мод с наибольшим числом участников реферера)
  mod_ids: z.array(z.coerce.string()),
  period_start: z.string(),          // referral_day: день; referral_iter: '' (ключ — итерация + транш)
  period_end: z.string(),
  tranche: z.number().int().min(1),  // referral_iter: номер сотни; referral_day: 1
  people: z.number(),
  locations: z.number(),
  amount_usd: z.number(),
  paid_usd: z.number(),
  paid_tx_id: z.string(),
});
export type JavPayoutReferral = z.infer<typeof JavPayoutReferralSchema>;

export const JavPayoutsDashboardResSchema = z.looseObject({
  iteration: z.string(),
  base_usd: z.number(),
  mods: z.array(z.looseObject({
    mod_id: z.coerce.string(),
    name: z.string(),
    is_new: z.boolean(),                 // мод первой итерации → пороги 94/96 (иначе 95/97)
    iteration_period: JavPayoutPeriodSchema,
    weeks: z.array(JavPayoutPeriodSchema),
  })),
  referrals: z.array(JavPayoutReferralSchema),
  /** Счётчики рефералов, не дотянувшие до бонуса — чтобы дашборд был не пустым. */
  referrers: z.array(z.looseObject({
    referrer_key: z.string(),
    referrer_phone_masked: z.string(),
    referrer_name: z.string(),
    people_iteration: z.number(),
    locations: z.number(),     // уникальных home_id у участников реферера
    base_usd: z.number(),      // справочно: $15 × люди + $10 × локации — платит мод из бюджета
    mod_ids: z.array(z.coerce.string()),
  })),
});
export type JavPayoutsDashboardRes = z.infer<typeof JavPayoutsDashboardResSchema>;

// Сумму сервер пересчитывает сам по тем же правилам; клиентская нужна лишь
// как подтверждение «менеджер видел эту цифру» — расхождение → 409.
export const JavPayoutBonusPayReqSchema = z.object({
  kind: z.enum(PAYOUT_KIND),
  iteration: z.string().trim().min(1),
  mod_id: z.string().trim().min(1),
  period_start: z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/),
  period_end: z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/),
  tranche: z.number().int().min(1).default(1),
  amount_usd: z.number().positive().max(10000),
  referrer_key: z.string().trim().max(64).default(''),
});
export type JavPayoutBonusPayReq = z.infer<typeof JavPayoutBonusPayReqSchema>;

export const JavPayoutBonusPayResSchema = z.looseObject({
  ok: z.literal(true),
  tx_id: z.string(),
  amount_usd: z.number(),
  idempotent_replay: z.boolean().default(false),
});
export type JavPayoutBonusPayRes = z.infer<typeof JavPayoutBonusPayResSchema>;

export const JavPayoutBonusDeleteReqSchema = z.object({
  tx_id: z.string().trim().min(1),
});
export type JavPayoutBonusDeleteReq = z.infer<typeof JavPayoutBonusDeleteReqSchema>;

export const JavPayoutBonusDeleteResSchema = z.looseObject({ ok: z.literal(true) });
export type JavPayoutBonusDeleteRes = z.infer<typeof JavPayoutBonusDeleteResSchema>;
